import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "family_token";
const REFRESH_KEY = "family_refresh_token";
const USER_KEY = "family_user";
const OSPITE_KEY = "family_active_ospite";
const FamilyAuthContext = createContext(null);

function stored(key, fallback = null) {
  try { return JSON.parse(localStorage.getItem(key) || "null") ?? fallback; } catch { return fallback; }
}

export function FamilyAuthProvider({ children }) {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(() => stored(USER_KEY));
  const [activeOspiteId, setActiveOspiteIdState] = useState(() => localStorage.getItem(OSPITE_KEY));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    [TOKEN_KEY, REFRESH_KEY, USER_KEY, OSPITE_KEY].forEach((key) => localStorage.removeItem(key));
    setToken(null); setUser(null); setActiveOspiteIdState(null);
    navigate("/family-login");
  }, [navigate]);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    axios.get(`${API_URL}/api/family/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        setUser(data);
        localStorage.setItem(USER_KEY, JSON.stringify(data));
        const permitted = (data.deleghe || []).find((d) => d.fascicolo_id === activeOspiteId) || data.deleghe?.[0];
        if (permitted) {
          setActiveOspiteIdState(permitted.fascicolo_id);
          localStorage.setItem(OSPITE_KEY, permitted.fascicolo_id);
        }
      })
      .catch(async (error) => {
        if (error.response?.status !== 401 || !localStorage.getItem(REFRESH_KEY)) throw error;
        const { data } = await axios.post(`${API_URL}/api/family/auth/refresh`, { refresh_token: localStorage.getItem(REFRESH_KEY) });
        localStorage.setItem(TOKEN_KEY, data.token); setToken(data.token);
      })
      .catch(logout)
      .finally(() => setLoading(false));
  }, [token, activeOspiteId, logout]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API_URL}/api/family/auth/login`, { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(REFRESH_KEY, data.refresh_token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    const ospite = data.user?.deleghe?.[0]?.fascicolo_id;
    if (ospite) localStorage.setItem(OSPITE_KEY, ospite);
    setToken(data.token); setUser(data.user); setActiveOspiteIdState(ospite || null);
    toast.success("Benvenuto in AFFIDYA Family");
    navigate("/family");
  }, [navigate]);

  const setActiveOspiteId = useCallback((id) => {
    localStorage.setItem(OSPITE_KEY, id);
    setActiveOspiteIdState(id);
  }, []);

  const refreshing = useRef(null);
  const refreshAccessToken = useCallback(() => {
    // una sola richiesta di rinnovo alla volta anche con più chiamate API scadute insieme
    if (!refreshing.current) {
      const refreshToken = localStorage.getItem(REFRESH_KEY);
      if (!refreshToken) return Promise.reject(new Error("no refresh token"));
      refreshing.current = axios.post(`${API_URL}/api/family/auth/refresh`, { refresh_token: refreshToken })
        .then(({ data }) => {
          localStorage.setItem(TOKEN_KEY, data.token);
          setToken(data.token);
          return data.token;
        })
        .finally(() => { refreshing.current = null; });
    }
    return refreshing.current;
  }, []);

  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: `${API_URL}/api/family`,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    // Sessione scaduta (access token 30 min): rinnovo silenzioso e ripetizione della chiamata, altrimenti logout.
    instance.interceptors.response.use((response) => response, async (error) => {
      const original = error.config;
      if (error.response?.status !== 401 || !original || original._retried || !localStorage.getItem(REFRESH_KEY)) throw error;
      original._retried = true;
      let fresh;
      try {
        fresh = await refreshAccessToken();
      } catch {
        logout();
        throw error;
      }
      if (typeof original.headers?.set === "function") original.headers.set("Authorization", `Bearer ${fresh}`);
      else original.headers = { ...(original.headers || {}), Authorization: `Bearer ${fresh}` };
      return instance.request(original);
    });
    return instance;
  }, [token, refreshAccessToken, logout]);

  const updateUser = useCallback((nextUser) => {
    setUser(nextUser);
    localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
  }, []);
  const activeDelega = user?.deleghe?.find((d) => d.fascicolo_id === activeOspiteId) || user?.deleghe?.[0] || null;
  const hasPermission = useCallback((permission) => Boolean(activeDelega?.permessi?.includes(permission)), [activeDelega]);

  return <FamilyAuthContext.Provider value={{
    token, user, loading, login, logout, api, activeOspiteId, activeDelega,
    setActiveOspiteId, hasPermission, updateUser, isAuthenticated: Boolean(token),
  }}>{children}</FamilyAuthContext.Provider>;
}

export function useFamilyAuth() {
  const context = useContext(FamilyAuthContext);
  if (!context) throw new Error("useFamilyAuth must be used within FamilyAuthProvider");
  return context;
}
